import type { ResultsAiChatBody, ResultsAiStreamEvent } from 'definitions';
import type { SseFrame } from 'helpers/sse-buffer';
import { parseSseBuffer } from 'helpers/sse-buffer';

/**
 * The client half of `POST /api/results-ai/chat`: the answer streamed as server-sent events, read
 * with fetch (EventSource cannot POST or send a bearer token). Nothing here is React.
 */

const EVENT_TYPES: ReadonlySet<string> = new Set(['turn', 'status', 'text', 'usage', 'error', 'done']);

/** A frame as a typed event, or null when it is not one this client knows. Unknown frames are skipped. */
export const toResultsEvent = (frame: Readonly<SseFrame>): ResultsAiStreamEvent | null => {
  if (!EVENT_TYPES.has(frame.event)) return null;
  try {
    const parsed: unknown = JSON.parse(frame.data);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return (parsed as { type?: unknown }).type === frame.event ? (parsed as ResultsAiStreamEvent) : null;
  } catch {
    return null;
  }
};

const MAX_SERVER_MESSAGE = 300;

/** The server's own text, when it is short plain text written for the person. */
const usableServerText = (serverMessage: string | undefined): string | undefined => {
  const text = serverMessage?.trim();
  return text && text.length <= MAX_SERVER_MESSAGE && !text.startsWith('<') ? text : undefined;
};

export type ChatFailure = { message: string; retryable: boolean };

/**
 * What the person is told when the request itself is refused, before any event arrives. Only a 402
 * and a 409 use the server's wording (out of credits, the monthly cap with its reset date, or who is
 * asking about the experiment, all written for
 * the person); every other status gets a fixed message, so no raw server text reaches the panel.
 */
export const describeResultsChatFailure = (status: number, serverMessage?: string): ChatFailure => {
  if (status === 402) {
    return {
      message:
        usableServerText(serverMessage) ??
        'The assistant is not available: the account is out of AI credits, or read-only until billing is sorted out.',
      retryable: false,
    };
  }
  // The experiment's one question at a time is a colleague's: the server names them.
  if (status === 409) {
    return {
      message:
        usableServerText(serverMessage) ??
        'Someone is asking the assistant about this experiment. You can send yours once their answer is done.',
      retryable: true,
    };
  }
  if (status === 429)
    return { message: 'You are sending messages too quickly. Please wait a moment.', retryable: true };
  if (status === 401) return { message: 'Your session has expired. Please sign in again.', retryable: false };
  if (status === 400) return { message: 'This question could not be sent. Please try again.', retryable: true };
  if (status === 403 || status === 503)
    return { message: 'The assistant is not available right now.', retryable: false };
  return { message: 'Something went wrong. Please try again.', retryable: true };
};

export class ResultsChatError extends Error {
  readonly status: number;
  readonly retryable: boolean;

  constructor({ status, message, retryable }: ChatFailure & { status: number }) {
    super(message);
    this.status = status;
    this.retryable = retryable;
  }
}

export type StreamResultsChatParams = {
  baseUrl: string;
  token: string | null;
  /** The chat's route: the results chat's by default, the learnings Ask's (same events) otherwise. */
  path?: string;
  body: Readonly<ResultsAiChatBody> | Readonly<{ question: string }>;
  signal?: AbortSignal;
  onEvent: (event: ResultsAiStreamEvent) => void;
};

export async function streamResultsChat({ baseUrl, token, path, body, signal, onEvent }: StreamResultsChatParams) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${baseUrl}${path ?? '/results-ai/chat'}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const ownWords = response.status === 402 || response.status === 409;
    const serverMessage = ownWords ? await response.text().catch(() => undefined) : undefined;
    throw new ResultsChatError({
      status: response.status,
      ...describeResultsChatFailure(response.status, serverMessage),
    });
  }
  if (!response.body) {
    throw new ResultsChatError({
      status: response.status,
      message: 'Something went wrong. Please try again.',
      retryable: true,
    });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: !done });
      const { frames, rest } = parseSseBuffer(done ? `${buffer}\n\n` : buffer);
      buffer = rest;
      for (const frame of frames) {
        const event = toResultsEvent(frame);
        if (event) onEvent(event);
      }
      if (done) {
        finished = true;
        return;
      }
    }
  } finally {
    // A handler that throws leaves the body unread; cancel it so the turn stops spending credits.
    if (!finished) await reader.cancel().catch(() => undefined);
  }
}
